import {useMemo, useEffect, useState} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import moment from 'moment-timezone'
import MessageConstants from '../Messages/MessageConstant'
import {getBuildingById} from '../../lib/queries'
import ActionTypes from '../../actions/types'

// 5 minutes in milliseconds
const REFRESH_INTERVAL = 5 * 60 * 1000

/**
 * Hook that determines if the building chat is currently attended based on desk hours.
 * Mirrors the logic in RXOHome/helpers/appQueries.js getBuildingOOOMessage function.
 *
 * Refreshes building data every 5 minutes to check for changes to deskHoursEnabled or outOfOfficeStatus,
 * and rechecks schedule logic when desk hours are enabled.
 *
 * @returns {string|null} - Returns an out of office message if the building is currently unattended, or null if it is attended
 */
function useIsBuildingChatAttended() {
  const dispatch = useDispatch()
  const activeBuildingId = useSelector(state => state.Buildings.activeBuildingId)
  const activeBuilding = useSelector(state => state.Buildings.buildingsLookup[activeBuildingId])

  // Used to force recalculation of the schedule check on each interval tick
  const [refreshTick, setRefreshTick] = useState(0)

  // Set up the refresh interval
  useEffect(() => {
    if (!activeBuildingId) {
      return
    }

    const refreshBuildingData = async () => {
      try {
        const updatedBuilding = await getBuildingById(activeBuildingId)
        if (updatedBuilding) {
          dispatch({
            type: ActionTypes.BUILDINGS_UPDATE,
            payload: updatedBuilding,
          })
        }
      } catch (err) {
        // Silently fail - we'll try again on the next interval
        console.warn('Failed to refresh building data for chat attendance check:', err)
      }
      // Increment tick to force recalculation even if building data didn't change
      // (handles time-based schedule changes)
      setRefreshTick(t => t + 1)
    }

    const intervalId = setInterval(refreshBuildingData, REFRESH_INTERVAL)

    return () => {
      clearInterval(intervalId)
    }
  }, [activeBuildingId, dispatch])

  return useMemo(() => {
    if (!activeBuilding) {
      return null
    }

    const oooMessage = activeBuilding.outOfOfficeMessage || MessageConstants.DEFAULT_OOO_MESSAGE

    // If desk hours are not enabled, use the outOfOfficeStatus flag
    // Note: outOfOfficeStatus is backwards - true means IN office, false means OUT of office
    if (!activeBuilding.deskHoursEnabled) {
      if (activeBuilding.outOfOfficeStatus) {
        return null
      }
      return oooMessage
    }

    // Desk hours are enabled, calculate based on schedule
    const buildingTimezone = activeBuilding.timezone || 'America/New_York'
    const now = moment().tz(buildingTimezone)

    const schedule = activeBuilding.deskHours?.schedule
    const exceptions = activeBuilding.deskHours?.exceptions

    // Find today's schedule
    const todaySchedule = schedule?.find(s => s.dayOfWeek === now.weekday())
    if (!todaySchedule) {
      // No schedule for today, assume closed
      return oooMessage
    }

    // Check for exceptions on today's date
    const exception = exceptions?.find(e => moment.tz(e.date, buildingTimezone).isSame(now, 'day'))

    if (exception) {
      if (exception.isClosedAllDay) {
        return oooMessage
      }
      const openTime = now.clone().startOf('day').add(exception.openTime, 'minutes')
      const closeTime = now.clone().startOf('day').add(exception.closeTime, 'minutes')
      if (now.isBefore(openTime) || now.isAfter(closeTime)) {
        return oooMessage
      }
      return null
    }

    // No exception, use regular schedule
    const openTime = now.clone().startOf('day').add(todaySchedule.openTime, 'minutes')
    const closeTime = now.clone().startOf('day').add(todaySchedule.closeTime, 'minutes')
    if (now.isBefore(openTime) || now.isAfter(closeTime)) {
      return oooMessage
    }

    return null
  }, [activeBuilding, refreshTick])
}

export default useIsBuildingChatAttended
